/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useContext, type ReactNode } from "react";

import { cssFns, type Style } from "@superweb/css";
import {
  Button,
  LinkButton,
  serviceIcons,
  useTypo,
  useUiColors,
} from "@superweb/ui";
import { useFlag } from "@superweb/flags";

import type { ApiResponse } from "@fleet/api";

import { useLogAction } from "#internal/analytics/logActions";
import {
  AppLayout,
  Form,
  NotFound,
  Offer,
  OfferDeprecated,
} from "#internal/ui";
import { Message, useMessage } from "#internal/intl";
import SerbiaOfferPng from "#internal/images/serbia-offer.png";
import SerbiaOfferWebp from "#internal/images/serbia-offer.webp";
import SerbiaOfferAvif from "#internal/images/serbia-offer.avif";

import { config } from "#internal/config/index";
import { AppLocaleContext } from "#internal/app-locale";

import { getSteps } from "../steps";

const deliveryProfessionIds = new Set([
  "cargo_courier_on_foot",
  "cargo_courier_on_bicycle",
  "cargo_courier_on_scooter",
  "cargo_courier_on_moto",
  "cargo_courier_on_car",
  "cargo_courier_on_truck",
]);

export const RegisterForm = ({
  parkInfo,
  isDelivery: isDeliveryDeprecated,
  formErrorMessage,
  setFormErrorMessage,
  content,
  onSubmit,
  isLoading,
  isMultipleProfessions,
  isMultipleEmploymentTypes,
  isIframe,
}: {
  isDelivery: boolean;
  formErrorMessage?: string;
  setFormErrorMessage: (errorMessage: string) => void;
  parkInfo?: Extract<
    ApiResponse<"/fleet-forms/selfreg/v1/park/info", "GET">,
    { status: 200 }
  >["body"];
  content: ReactNode;
  onSubmit: () => void;
  isLoading: boolean;
  isMultipleProfessions?: boolean;
  isMultipleEmploymentTypes?: boolean;
  isIframe: boolean;
}) => {
  const message = useMessage();
  const flag = useFlag();

  const typo = useTypo();
  const uiColors = useUiColors();

  const { appLocale } = useContext(AppLocaleContext);
  const logActionContact = useLogAction("contact");

  const { stepsCount, currentStep } = getSteps({
    isMultipleProfessions,
    isMultipleEmploymentTypes,
    formRequirements: parkInfo?.form_requirements,
    currentStep: "register",
  });

  if (!parkInfo) {
    return <NotFound />;
  }

  const linkStyles: Style = {
    color: uiColors.text,
    ...cssFns.border({ radius: "6px" }),
    ...typo({
      level: "caption1",
      weight: "regular",
      density: "tight",
    }),
    outlineStyle: "none",
    cursor: "pointer",
  };

  const title = message({
    id: "9ff113b4-d40a-4b16-8d13-10b7897e71fd",
    context: "Yango forms. Header title",
    default: "Registration",
  });

  const retpathUrl = new URL(window.location.href);
  retpathUrl.searchParams.set("pro_id", "true");
  retpathUrl.searchParams.set("lang", appLocale.toString());

  const passportUrl = `${config.passportURL}/auth?${new URLSearchParams({
    retpath: retpathUrl.toString(),
    origin: "fleet-forms",
  })}`;

  const isDelivery =
    !isMultipleProfessions &&
    deliveryProfessionIds.has(
      parkInfo.profession_settings_list[0]?.profession_id ?? "",
    );
  const offerType = isMultipleProfessions
    ? "multiple"
    : isDelivery
      ? "delivery"
      : "taxi";

  const enableNewOffer = flag({
    id: "7c260e68-0d50-46ef-9d3e-0f1855147e2d",
    description: "Enable new offer",
  });
  const enableSerbiaOfferImage =
    parkInfo.park_id === "1a1d6c5f1ad7423481a1bc8a8c5d09db";

  return (
    <AppLayout
      title={isIframe ? undefined : title}
      partnerName={parkInfo.partner_name}
      content={
        <Form
          header={{
            title,
            ...(currentStep &&
              stepsCount && {
                step: {
                  current: currentStep,
                  total: stepsCount,
                },
              }),
          }}
          content={content}
          onSubmit={onSubmit}
          footer={{
            main: (
              <>
                <LinkButton
                  link={{ href: passportUrl }}
                  icon={serviceIcons.YandexPro}
                  text={message({
                    id: "ec996fc3-697d-45b9-a005-63e206904d51",
                    context: "Fleet forms. Pro link button text",
                    default: "I already have a Pro profile",
                  })}
                  size="l"
                />
                <Button
                  variant="action"
                  type="submit"
                  shape="squircle"
                  progress={isLoading}
                  text={message({
                    id: "0a74b8f4-b50e-4465-8321-f30a3efe0ca5",
                    context: "Yango forms. Submit button text",
                    default: "Register",
                  })}
                  onPress={() => setFormErrorMessage("")}
                  size="l"
                />
              </>
            ),
            bottomSheet: (
              <Message
                id="ecf94e61-35e2-404b-bec3-9faef2c5295f"
                context="Yango forms. Agree processing data. Title"
                default="By submitting this form, I accept the <terms_of_use>Terms Of Use</terms_of_use>, agree to the processing of my personal data for the purpose of reviewing my application, and confirm that I have read the <privacy_notice>Privacy Policy</privacy_notice>."
                __experimental_tags={{
                  terms_of_use: (chunks: ReactNode) => (
                    <a
                      target="_blank"
                      href={parkInfo.legal_docs_urls.url_terms_of_use}
                      css={linkStyles}
                      key="terms_of_use"
                    >
                      {chunks}
                    </a>
                  ),
                  privacy_notice: (chunks: ReactNode) => (
                    <a
                      target="_blank"
                      href={parkInfo.legal_docs_urls.url_privacy_policy}
                      css={linkStyles}
                      key="privacy_notice"
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            ),
            errorMessage: formErrorMessage,
          }}
        />
      }
      offer={
        isIframe ? undefined : enableNewOffer ? (
          <Offer
            offerText={parkInfo.promo_text}
            image={
              enableSerbiaOfferImage
                ? {
                    avif: SerbiaOfferAvif,
                    png: SerbiaOfferPng,
                    webp: SerbiaOfferWebp,
                    height: "130px",
                    width: "178px",
                    padding: "24px",
                  }
                : undefined
            }
            type={offerType}
            onPhonePress={() =>
              logActionContact("phone_click_v2", {
                params: { stage: "register" },
              })
            }
            onMessengerPress={() =>
              logActionContact("messenger_click_v2", {
                params: { stage: "register" },
              })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        ) : (
          <OfferDeprecated
            parkId={parkInfo.park_id}
            offerText={parkInfo.promo_text}
            isDelivery={isDeliveryDeprecated}
            onPhonePress={() =>
              logActionContact("phone_click_v2", {
                params: { stage: "register" },
              })
            }
            onMessengerPress={() =>
              logActionContact("messenger_click_v2", {
                params: { stage: "register" },
              })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        )
      }
    />
  );
};
